import {useState, useEffect, useCallback} from 'react'
import PropTypes from 'prop-types'
import {
  Grid,
  GridItem,
  Modal,
  ModalBody,
  Flex,
  Heading,
  Text,
  ModalOverlay,
  ModalContent,
} from '@chakra-ui/react'
import {useLocation, useNavigate} from 'react-router'
import {useTranslation} from 'react-i18next'

import StepDisplay from './StepDisplay'
import TemplateModalStepContent from './TemplateModalStepContent'
import ReviewModalStepContent from './ReviewModalStepContent'
import {useRails} from '../../contexts/rails'
import patternBackground from '../../../images/Pattern_1.svg'
import {
  MyobLogoInCircleIcon,
  XeroLogoInCircleIcon,
  QuickbooksLogoInCircleIcon,
} from '../../icons'
import {refreshStepperData} from '../CashinStepper/useStepperData'

const HELP_ARTICLE_URLS = {
  invoice: {
    quickbooks:
      'https://marmalade.ladesk.com/593884-Updating-Invoice-Templates-in-QuickBooks?r=1',
    myob: 'https://marmalade.ladesk.com/147626-Updating-Invoice-Templates-in-MYOB?r=1',
    xero: 'https://marmalade.ladesk.com/882629-Updating-Invoice-Templates-in-Xero?r=1',
  },
  email: {
    quickbooks:
      'https://marmalade.ladesk.com/580932-Updating-Email-Templates-in-QuickBooks?r=1',
    myob: 'https://marmalade.ladesk.com/365797-Updating-Email-Template-in-MYOB?r=1',
    xero: 'https://marmalade.ladesk.com/576196-Updating-Email-Templates-in-Xero?r=1',
  },
}

const PROVIDER_ICONS = {
  quickbooks: <QuickbooksLogoInCircleIcon boxSize="16" ml="-2" />,
  myob: <MyobLogoInCircleIcon boxSize="16" ml="-2" />,
  xero: <XeroLogoInCircleIcon boxSize="16" ml="-2" />,
}

const invoiceEmailTemplateSteps = [
  {
    id: 1,
    nextStep: 'email',
    currentStep: 'invoice',
    previousStep: null,
  },
  {
    id: 2,
    nextStep: 'review',
    currentStep: 'email',
    previousStep: 'invoice',
  },
  {
    id: 3,
    nextStep: null,
    currentStep: 'review',
    previousStep: 'email',
  },
]

const InvoiceEmailTemplateModals = ({isOpen, onClose}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const {t} = useTranslation('invoiceEmailTemplates')
  const {
    organisation: {currentSupplier},
  } = useRails()

  const provider = currentSupplier.accountingProvider.name.toLowerCase()

  const handleClose = () => {
    // Refresh stepper data when closing the modal
    refreshStepperData()
    onClose()
  }
  const getCurrentStepFromPath = useCallback(() => {
    const pathParts = location.pathname.split('/')
    const lastPart = pathParts[pathParts.length - 1]

    // if no specified steps, go to invoice
    if (lastPart === 'setup_templates' || !lastPart) {
      return 'invoice'
    }

    return lastPart.replace(/-/g, '_')
  }, [location.pathname])

  const [step, setStep] = useState(getCurrentStepFromPath())

  // Update step when path changes
  useEffect(() => {
    setStep(getCurrentStepFromPath())
  }, [location.pathname, getCurrentStepFromPath])

  // Get current step index
  const currentStepObj = invoiceEmailTemplateSteps.find(
    (s) => s.currentStep === step,
  )

  const goToNextStep = (nextStep) => {
    const targetStep = nextStep || currentStepObj.nextStep
    if (targetStep) {
      navigate(`/setup_templates/${targetStep}`)
    }
  }

  const goToPreviousStep = (previousStep) => {
    const targetStep = previousStep || currentStepObj.previousStep
    if (targetStep) {
      navigate(`/setup_templates/${targetStep}`)
    }
  }

  const getArticleUrl = () => {
    const providerUrls = HELP_ARTICLE_URLS[step]

    return providerUrls[provider] || providerUrls.xero
  }

  const getProviderIcon = () => {
    return PROVIDER_ICONS[provider] || PROVIDER_ICONS.xero
  }

  const providerIcon = getProviderIcon()

  return (
    <Modal size="5xl" isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent borderRadius="lg" height="650px">
        <Grid templateColumns="repeat(3, 1fr)" height="full">
          {/* Introduction Section */}
          <GridItem
            alignContent="center"
            backgroundImage={patternBackground}
            backgroundColor="black"
            backgroundRepeat="repeat-x"
            backgroundSize="auto 100%"
            borderLeftRadius="lg"
            colSpan={1}
          >
            <ModalBody align="center" height="full">
              <Flex direction="column" justify="space-between" height="full">
                <Flex
                  height={
                    currentStepObj.currentStep === 'review' ? '16px' : '52px'
                  }
                />
                <Flex direction="column" px={4} gap="xs" justify="center">
                  <Heading
                    color="neutral.200"
                    fontWeight="normal"
                    lineHeight="90%"
                    align="center"
                    textTransform="uppercase"
                  >
                    {t('introduction.title', {provider})}
                  </Heading>
                  <Text
                    color="mld.neutral.200"
                    align="center"
                    textTransform="uppercase"
                    fontSize="sm"
                  >
                    {t('introduction.subtitle')}
                  </Text>
                  <Text
                    color="mld.neutral.500"
                    weight="medium"
                    align="center"
                    fontSize="xs"
                  >
                    {t('introduction.description')}
                  </Text>
                </Flex>
                <StepDisplay
                  currentStepId={currentStepObj.id}
                  steps={invoiceEmailTemplateSteps}
                />
              </Flex>
            </ModalBody>
          </GridItem>
          <GridItem colSpan={2} position="relative">
            {step !== 'review' ? (
              <TemplateModalStepContent
                goToPreviousStep={goToPreviousStep}
                goToPreviousStepEnabled={currentStepObj.currentStep === 'email'}
                goToNextStep={goToNextStep}
                articleUrl={getArticleUrl()}
                providerIcon={providerIcon}
                currentStepObj={currentStepObj}
              />
            ) : (
              <ReviewModalStepContent
                onClose={handleClose}
                provider={provider}
              />
            )}
          </GridItem>
        </Grid>
      </ModalContent>
    </Modal>
  )
}

InvoiceEmailTemplateModals.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default InvoiceEmailTemplateModals
